import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import { reportingService } from '../../../services/api';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, LineChart, Line, Area,
} from 'recharts';
import {
    C, FONT, PIE_COLORS, PRESETS,
    getToday, getStartOfWeek, getEndOfWeek, getStartOfMonth, getStartOfYear,
    formatDateLabel, formatWeekLabel, formatMonthLabel, toLocalISO, fillDateRange,
    fmt, sectionLabel,
    Sparkline, DownloadIcon, ExpandIcon, InfoIcon, WarningIcon, TrendUpIcon, TargetIcon,
    EmptyState, BarListItem, SectionCard, SectionDivider,
} from './_shared';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileCard from '../../../components/common/MobileCard';
import MobileHero from '../../../components/common/MobileHero';
import MobileBanner from '../../../components/common/MobileBanner';

/* ══════════════════════════════════════════════════════════════
   Main Component
   ══════════════════════════════════════════════════════════════ */
export default function FinancialPerformancePage({ parentDates } = {}) {
    const { getCurrency } = useAuth();
    const currency = getCurrency ? getCurrency() : '';
    const { t } = useLanguage();
    const isMobile = useIsMobile();

    /* ── State ── */
    const [preset, setPreset] = useState('week');
    const [customStart, setCustomStart] = useState(getToday);
    const [customEnd, setCustomEnd] = useState(getToday);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [narrative, setNarrative] = useState(null);
    const [targets, setTargets] = useState(null);
    const [flippedCards, setFlippedCards] = useState({});
    const [expandedAnomalies, setExpandedAnomalies] = useState(false);
    const [chartModal, setChartModal] = useState(false);
    const [modalOffset, setModalOffset] = useState(0);
    const [modalData, setModalData] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [targetModal, setTargetModal] = useState(false);
    const [targetForm, setTargetForm] = useState({ revenue: '', profit: '', margin: '', avg_transaction: '' });
    const flipTimers = useRef({});

    /* ── Date range ── */
    const internalRange = useMemo(() => {
        switch (preset) {
            case 'today': return { start: getToday(), end: getToday() };
            case 'week':  return { start: getStartOfWeek(), end: getEndOfWeek() };
            case 'month': return { start: getStartOfMonth(), end: getToday() };
            case 'year':  return { start: getStartOfYear(), end: getToday() };
            case 'custom': return { start: customStart, end: customEnd };
            default: return { start: getToday(), end: getToday() };
        }
    }, [preset, customStart, customEnd]);
    const dateRange = parentDates || internalRange;

    /* ── Fetch financial data ── */
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await reportingService.getFinancialV2(dateRange.start, dateRange.end);
            setData(result);
        } catch (err) {
            console.error('Financial data fetch error:', err);
        }
        setLoading(false);
    }, [dateRange.start, dateRange.end]);

    useEffect(() => { fetchData(); }, [fetchData]);

    /* ── Fetch narrative ── */
    useEffect(() => {
        reportingService.getNarrativeV2(dateRange.start, dateRange.end)
            .then(res => setNarrative(res))
            .catch(() => setNarrative(null));
    }, [dateRange.start, dateRange.end]);

    /* ── Fetch targets ── */
    useEffect(() => {
        reportingService.getTargetsV2()
            .then(res => setTargets(res))
            .catch(() => setTargets(null));
    }, []);

    /* ── KPI card flip ── */
    const toggleFlip = (idx) => {
        const isFlipped = flippedCards[idx];
        setFlippedCards(prev => ({ ...prev, [idx]: !prev[idx] }));
        if (flipTimers.current[idx]) clearTimeout(flipTimers.current[idx]);
        if (!isFlipped) {
            flipTimers.current[idx] = setTimeout(() => {
                setFlippedCards(prev => ({ ...prev, [idx]: false }));
            }, 5000);
        }
    };

    /* ── Modal period navigation ── */
    const getOffsetRange = useCallback((offset) => {
        const shiftDate = (dateStr, days) => {
            const d = new Date(dateStr + 'T12:00:00');
            d.setDate(d.getDate() + days);
            return toLocalISO(d);
        };
        const dayCount = Math.round((new Date(dateRange.end + 'T12:00:00') - new Date(dateRange.start + 'T12:00:00')) / 86400000) + 1;
        const shift = offset * dayCount;
        return {
            start: shiftDate(dateRange.start, shift),
            end: shiftDate(dateRange.end, shift),
        };
    }, [dateRange]);

    const modalRange = useMemo(() => getOffsetRange(modalOffset), [getOffsetRange, modalOffset]);

    const modalRangeLabel = useMemo(() => {
        const f = (s) => new Date(s + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
        return `${f(modalRange.start)} – ${f(modalRange.end)}`;
    }, [modalRange]);

    useEffect(() => {
        if (!chartModal) return;
        setModalLoading(true);
        reportingService.getFinancialV2(modalRange.start, modalRange.end)
            .then(result => {
                const raw = result?.sections?.revenue_trend || [];
                const type = result?.sections?.revenue_trend_type;
                if (type === 'hourly') {
                    const hourMap = {};
                    raw.forEach(r => { hourMap[r.hour] = r; });
                    setModalData(Array.from({ length: 24 }, (_, h) => ({
                        label: String(h).padStart(2, '0') + ':00',
                        revenue: Number(hourMap[h]?.revenue || 0),
                    })));
                } else if (type === 'weekly') {
                    setModalData(raw.map(r => ({
                        label: formatWeekLabel(r.week_start),
                        revenue: Number(r.revenue || 0),
                    })));
                } else if (type === 'monthly') {
                    setModalData(raw.map(r => ({
                        label: formatMonthLabel(r.month_start),
                        revenue: Number(r.revenue || 0),
                    })));
                } else {
                    setModalData(fillDateRange(raw, modalRange.start, modalRange.end).map(r => ({
                        label: formatDateLabel(r.sales_date),
                        revenue: Number(r.revenue || 0),
                    })));
                }
            })
            .catch(() => setModalData([]))
            .finally(() => setModalLoading(false));
    }, [chartModal, modalRange]);

    const openModal = () => { setModalOffset(0); setChartModal(true); };

    /* ── Label helpers ── */
    const fmtDate = (s) => new Date(s + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    const currentLabel = `${fmtDate(dateRange.start)} – ${fmtDate(dateRange.end)}`;
    const prevLabel = data?.comparison_period
        ? `${fmtDate(data.comparison_period.start)} – ${fmtDate(data.comparison_period.end)}`
        : '';

    const sections = data?.sections || {};
    const kpis = data?.kpis || [];
    const kpiDescriptions = narrative?.kpi_descriptions || {};
    const anomalies = narrative?.anomalies || [];
    const visibleAnomalies = expandedAnomalies ? anomalies : anomalies.slice(0, 3);

    /* ── Revenue Trend — smart granularity ── */
    const rawTrend = sections.revenue_trend || [];
    const trendType = sections.revenue_trend_type || 'daily';

    let revenueTrend;
    if (trendType === 'hourly') {
        const hourMap = {};
        rawTrend.forEach(r => { hourMap[r.hour] = r; });
        revenueTrend = Array.from({ length: 24 }, (_, h) => ({
            label: String(h).padStart(2, '0') + ':00',
            revenue: Number(hourMap[h]?.revenue || 0),
            transactions: Number(hourMap[h]?.transactions || 0),
        }));
    } else if (trendType === 'weekly') {
        revenueTrend = rawTrend.map(r => ({
            label: formatWeekLabel(r.week_start),
            revenue: Number(r.revenue || 0),
            transactions: Number(r.transactions || 0),
        }));
    } else if (trendType === 'monthly') {
        revenueTrend = rawTrend.map(r => ({
            label: formatMonthLabel(r.month_start),
            revenue: Number(r.revenue || 0),
            transactions: Number(r.transactions || 0),
        }));
    } else {
        revenueTrend = fillDateRange(rawTrend, dateRange.start, dateRange.end).map(r => ({
            label: typeof r.sales_date === 'string' && r.sales_date.length >= 10 ? formatDateLabel(r.sales_date) : String(r.sales_date),
            revenue: Number(r.revenue || 0),
            transactions: Number(r.transactions || 0),
        }));
    }

    /* ── Payment Breakdown ── */
    const paymentData = (sections.payment_breakdown || []).map(p => ({
        name: p.payment_method,
        value: Number(p.revenue),
        count: p.transactions,
    }));

    /* ── Dining Split — all dining options ── */
    const DINING_COLORS = [C.green, C.blue, C.amber, C.purple, '#0891b2', '#dc2626'];
    const diningItems = (sections.dining_options || [])
        .filter(d => d.dining_option && d.dining_option !== 'Unknown')
        .map((d, i) => ({
            label: d.dining_option,
            value: Number(d.revenue || 0),
            count: Number(d.count || 0),
            color: DINING_COLORS[i % DINING_COLORS.length],
        }));
    const diningMax = Math.max(...diningItems.map(d => d.value), 1);

    /* ── Expense Categories — grouped by type ── */
    const rawExpenses = (sections.expense_categories || [])
        .filter(e => e.category && !String(e.category).toLowerCase().includes('null'));
    const overheadExpenses = rawExpenses.filter(e => e.expense_type === 'overhead').sort((a, b) => Number(b.total_cost) - Number(a.total_cost));
    const dailyExpenses = rawExpenses.filter(e => e.expense_type !== 'overhead').sort((a, b) => Number(b.total_cost) - Number(a.total_cost));
    const expenseData = rawExpenses.map(e => ({ label: e.category, value: Number(e.total_cost), type: e.expense_type })).sort((a, b) => b.value - a.value);
    const expenseMax = Math.max(...expenseData.map(e => e.value), 1);
    const expenseTotal = expenseData.reduce((s, e) => s + e.value, 0);

    /* ── Forecast data ── */
    const daysElapsed = Math.round((new Date(getToday() + 'T12:00:00') - new Date(dateRange.start + 'T12:00:00')) / 86400000) + 1;
    const totalDays = Math.round((new Date(dateRange.end + 'T12:00:00') - new Date(dateRange.start + 'T12:00:00')) / 86400000) + 1;
    const totalRevenue = kpis.find(k => k.name === 'Revenue')?.value || kpis[0]?.value || 0;
    const dailyAvg = daysElapsed > 0 ? totalRevenue / daysElapsed : 0;
    const projectedRevenue = dailyAvg * totalDays;
    const showForecast = (preset === 'month' || preset === 'custom') && totalDays > 14 && daysElapsed < totalDays;
    const forecastConfidence = Math.min(Math.round((daysElapsed / totalDays) * 100), 100);

    const forecastChartData = useMemo(() => {
        if (!showForecast || revenueTrend.length === 0) return [];
        let cumulative = 0;
        const chartData = revenueTrend.map((r, i) => {
            cumulative += r.revenue;
            return { label: r.label, actual: cumulative, forecast: null, forecastLow: null, forecastHigh: null };
        });
        // project remaining days
        const remaining = totalDays - daysElapsed;
        for (let i = 0; i < remaining; i++) {
            cumulative += dailyAvg;
            const low = cumulative * 0.85;
            const high = cumulative * 1.15;
            chartData.push({
                label: `+${i + 1}`,
                actual: null,
                forecast: cumulative,
                forecastLow: low,
                forecastHigh: high,
            });
        }
        return chartData;
    }, [showForecast, revenueTrend, totalDays, daysElapsed, dailyAvg]);

    /* ── Profit Waterfall ── */
    const waterfallSalary = expenseData.filter(e => e.label.toLowerCase().includes('salary') || e.label.toLowerCase().includes('payroll')).reduce((s, e) => s + e.value, 0);
    const waterfallShopping = expenseData.filter(e => e.label.toLowerCase().includes('shopping') || e.label.toLowerCase().includes('ingredient') || e.label.toLowerCase().includes('supply')).reduce((s, e) => s + e.value, 0);
    const waterfallOverhead = expenseTotal - waterfallSalary - waterfallShopping;
    const netProfit = totalRevenue - expenseTotal;

    /* ── Target progress ── */
    const targetsList = targets?.targets || [];
    const recommendations = targets?.recommendations || {};

    const getTargetProgress = (target) => {
        const actual = target.actual || 0;
        const goal = target.goal || 1;
        const pct = Math.min(Math.round((actual / goal) * 100), 100);
        return { pct, actual, goal };
    };

    const getTargetColor = (pct) => {
        if (pct >= 75) return C.green;
        if (pct >= 50) return C.amber;
        return C.red;
    };

    const handleSaveTargets = async () => {
        try {
            await reportingService.saveTargetsV2(targetForm);
            const res = await reportingService.getTargetsV2();
            setTargets(res);
            setTargetModal(false);
        } catch (err) {
            console.error('Save targets error:', err);
        }
    };

    /* ── Escape handler for target modal ── */
    useEffect(() => {
        if (!targetModal) return;
        const handler = (e) => { if (e.key === 'Escape') setTargetModal(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [targetModal]);

    /* ── Loading state ── */
    if (loading && !data) {
        return (
            <div style={{ fontFamily: FONT, fontSize: 14, color: C.muted, padding: '60px 0', textAlign: 'center' }}>
                {t('loading')}...
            </div>
        );
    }

    return (
        <div>
            {/* ═══ 1. Header (hidden when parent controls dates) ═══ */}
            {!parentDates && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>
                            {t('financial_performance')}
                        </h2>
                        <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginTop: 4 }}>
                            {t('revenue_profit_expense_trends')}
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            display: 'inline-flex', background: C.bg, borderRadius: 8,
                            border: '1.5px solid ' + C.border, overflow: 'hidden',
                        }}>
                            {PRESETS.map(p => (
                                <button
                                    key={p.key}
                                    onClick={() => setPreset(p.key)}
                                    style={{
                                        fontFamily: FONT, fontSize: 12, fontWeight: 600,
                                        padding: '7px 14px', border: 'none', cursor: 'pointer',
                                        background: preset === p.key ? C.green : 'transparent',
                                        color: preset === p.key ? C.white : C.muted,
                                        transition: 'all 0.15s',
                                    }}
                                >{t(p.label)}</button>
                            ))}
                        </div>
                        {preset === 'custom' && (
                            <>
                                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                                    style={{ fontFamily: FONT, fontSize: 12, padding: '6px 10px', border: '1.5px solid ' + C.border, borderRadius: 8, outline: 'none' }}
                                />
                                <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>{t('to')}</span>
                                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                                    style={{ fontFamily: FONT, fontSize: 12, padding: '6px 10px', border: '1.5px solid ' + C.border, borderRadius: 8, outline: 'none' }}
                                />
                            </>
                        )}
                        <button
                            onClick={() => {/* PDF export */}}
                            style={{
                                fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '8px 16px', border: '1.5px solid ' + C.border, borderRadius: 8,
                                background: C.white, color: C.text, cursor: 'pointer', transition: 'all 0.15s',
                                whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = C.bg; }}
                            onMouseLeave={e => { e.currentTarget.style.background = C.white; }}
                        >
                            <DownloadIcon /> {t('export_pdf')}
                        </button>
                    </div>
                </div>
            )}

            {/* ═══ 2. AI Narrative Card ═══ */}
            <SectionCard style={{
                background: 'linear-gradient(135deg, #eff5ff 0%, #ffffff 100%)',
                border: '1.5px solid ' + C.greenBorder,
                borderRadius: 14,
                marginBottom: 20,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                    <span style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.greenDark }}>
                        {t('ai_financial_briefing')}
                    </span>
                </div>
                <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 500, color: C.text, lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{
                        __html: narrative?.narrative
                            ? narrative.narrative.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            : t('financial_briefing_fallback')
                    }}
                />
                <div style={{ fontFamily: FONT, fontSize: 10, color: C.mutedLight, marginTop: 12 }}>
                    {t('auto_generated')}
                    {narrative?.generated_at && ` · ${t('updated')} ${(() => {
                        const diff = Math.round((Date.now() - new Date(narrative.generated_at).getTime()) / 3600000);
                        return diff < 1 ? t('just_now') : `${diff}h ${t('ago')}`;
                    })()}`}
                    {` · ${currentLabel}`}
                </div>
            </SectionCard>

            {/* ═══ 3. Anomaly Alert Bar ═══ */}
            {anomalies.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                    {visibleAnomalies.map((a, i) => {
                        const isWarning = a.type === 'warning' || a.severity === 'warning';
                        const bg = isWarning ? C.amberLight : C.blueLight;
                        const borderColor = isWarning ? C.amberBorder : C.blueBorder;
                        const iconColor = isWarning ? C.amber : C.blue;
                        return (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '12px 16px', borderRadius: 10,
                                background: bg, border: '1px solid ' + borderColor,
                            }}>
                                <div style={{ color: iconColor, flexShrink: 0 }}>
                                    {isWarning ? <WarningIcon /> : <TrendUpIcon />}
                                </div>
                                <div style={{ flex: 1, fontFamily: FONT, fontSize: 13, fontWeight: 500, color: C.text }}>
                                    {a.message}
                                </div>
                                <button style={{
                                    fontFamily: FONT, fontSize: 12, fontWeight: 600, color: iconColor,
                                    background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                                }}>
                                    {t('investigate')} →
                                </button>
                            </div>
                        );
                    })}
                    {anomalies.length > 3 && (
                        <button
                            onClick={() => setExpandedAnomalies(!expandedAnomalies)}
                            style={{
                                fontFamily: FONT, fontSize: 12, fontWeight: 600, color: C.green,
                                background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center',
                            }}
                        >
                            {expandedAnomalies ? t('show_less') : `${t('show_all')} (${anomalies.length})`}
                        </button>
                    )}
                </div>
            )}

            {/* ═══ 4. KPI Cards ═══ */}
            {isMobile ? (
                kpis.length > 0 && (() => {
                    const head = kpis[0];
                    const fmtKpi = (k) => k.is_percentage
                        ? `${Number(k.value || 0).toFixed(1)}%`
                        : fmt(k.value, currency);
                    return (
                        <MobileHero
                            label={head.name}
                            value={fmtKpi(head)}
                            alert={head.invert ? (head.change_percent || 0) > 0 : (head.change_percent || 0) < 0}
                            meta={head.change_percent != null
                                ? `${(head.change_percent || 0) >= 0 ? '↑' : '↓'} ${Math.abs(head.change_percent || 0).toFixed(1)}% ${currentLabel || ''}`
                                : (currentLabel || '')}
                            stats={kpis.slice(1, 5).map(k => ({
                                label: k.name,
                                value: fmtKpi(k),
                                tone: k.change_percent == null
                                    ? undefined
                                    : ((k.invert ? (k.change_percent || 0) <= 0 : (k.change_percent || 0) >= 0) ? 'green' : 'red'),
                            }))}
                        />
                    );
                })()
            ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 14, marginBottom: 28 }}>
                {kpis.map((kpi, idx) => {
                    const isHero = idx === 0;
                    const flipped = flippedCards[idx];
                    const positive = kpi.invert ? (kpi.change_percent || 0) <= 0 : (kpi.change_percent || 0) >= 0;
                    const changeColor = positive ? C.green : C.red;
                    const arrow = (kpi.change_percent || 0) >= 0 ? '↑' : '↓';
                    const sparkData = kpi.sparkline || revenueTrend.map(r => r.revenue).slice(-7);
                    const descKeyMap = { 'Total Revenue': 'revenue', 'Net Profit': 'profit', 'Profit Margin': 'margin', 'Avg Transaction': 'avg_transaction', 'Total Expenses': 'expenses', 'Breakeven': 'breakeven', 'Rev vs Target': 'revenue' };
                    const descKey = descKeyMap[kpi.name] || kpi.name?.toLowerCase();
                    const description = kpiDescriptions[descKey] || kpiDescriptions[kpi.name] || kpiDescriptions[kpi.name?.toLowerCase()] || t('no_description_available');
                    const cardHeight = 125;

                    return (
                        <div key={kpi.name || idx} style={{ perspective: 1000, height: cardHeight }}>
                            <div style={{
                                transition: 'transform 0.3s ease-out',
                                transformStyle: 'preserve-3d',
                                transform: flipped ? 'rotateY(180deg)' : 'rotateY(0)',
                                position: 'relative', width: '100%', height: '100%',
                            }}>
                                {/* Front face */}
                                <div style={{
                                    backfaceVisibility: 'hidden', position: 'absolute', width: '100%', height: '100%',
                                    background: isHero ? C.greenLight : C.white,
                                    border: '1.5px solid ' + (isHero ? C.greenBorder : C.border),
                                    borderRadius: 12, padding: '12px 12px',
                                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                    boxSizing: 'border-box', overflow: 'hidden',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
                                        <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted }}>
                                            {kpi.name}
                                        </div>
                                        <button
                                            onClick={() => toggleFlip(idx)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: C.mutedLight, opacity: 0.7, flexShrink: 0 }}
                                            title={t('more_info')}
                                        >
                                            <InfoIcon />
                                        </button>
                                    </div>
                                    <div>
                                        <div style={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1.2, marginBottom: 4 }}>
                                            {kpi.is_percentage ? `${Number(kpi.value || 0).toFixed(1)}%` : fmt(kpi.value, currency)}
                                        </div>
                                        {kpi.change_percent != null ? (
                                            <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: changeColor }}>
                                                {arrow} {Math.abs(kpi.change_percent).toFixed(1)}%
                                            </div>
                                        ) : (
                                            <div style={{ fontFamily: FONT, fontSize: 12, color: C.mutedLight }}>—</div>
                                        )}
                                    </div>
                                    {kpi.change_absolute != null && (
                                        <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted }}>
                                            {kpi.change_absolute >= 0 ? '+' : ''}{kpi.is_percentage ? `${Number(kpi.change_absolute || 0).toFixed(1)}%` : fmt(kpi.change_absolute, currency)}
                                        </div>
                                    )}
                                    <div style={{ fontFamily: FONT, fontSize: 10, color: C.mutedLight }}>
                                        {prevLabel ? `vs ${prevLabel}` : currentLabel}
                                    </div>
                                </div>

                                {/* Back face */}
                                <div style={{
                                    backfaceVisibility: 'hidden', transform: 'rotateY(180deg)',
                                    position: 'absolute', width: '100%', height: '100%',
                                    background: C.white, border: '1.5px solid ' + C.border,
                                    borderRadius: 12, padding: '16px 18px',
                                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                    boxSizing: 'border-box',
                                }}>
                                    <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.green, marginBottom: 8 }}>
                                        {kpi.name}
                                    </div>
                                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                                        {description}
                                    </div>
                                    <button
                                        onClick={() => toggleFlip(idx)}
                                        style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.green, background: 'none', border: 'none', cursor: 'pointer', marginTop: 8, textAlign: 'left', padding: 0 }}
                                    >
                                        ← {t('back')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            )}

            {/* ═══ 5. Section Divider: Deep Dive ═══ */}
            <SectionDivider label={t('deep_dive')} tag={t('explorer')} />

            {/* ═══ 6. Explorer Layer ═══ */}
            {/* Revenue Trend + Expense Breakdown */}
            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <SectionCard>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <div style={sectionLabel}>{t('revenue_trend')}</div>
                            <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 16 }}>
                                {trendType === 'hourly' ? t('hourly_revenue_description')
                                    : trendType === 'weekly' ? t('weekly_revenue_description')
                                    : trendType === 'monthly' ? t('monthly_revenue_description')
                                    : t('revenue_trend_description')}
                            </div>
                        </div>
                        {revenueTrend.length > 0 && (
                            <button
                                onClick={openModal}
                                style={{
                                    background: 'none', border: '1px solid ' + C.border, borderRadius: 6,
                                    cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center',
                                    color: C.muted, marginBottom: 16,
                                }}
                                title={t('expand_chart')}
                            >
                                <ExpandIcon />
                            </button>
                        )}
                    </div>
                    {revenueTrend.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={revenueTrend} margin={{ top: 0, right: 0, left: -20, bottom: revenueTrend.length > 10 ? 30 : 0 }}>
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }}
                                    axisLine={false} tickLine={false}
                                    interval={trendType === 'hourly' ? 2 : 0}
                                    angle={revenueTrend.length > 10 ? -45 : 0}
                                    textAnchor={revenueTrend.length > 10 ? 'end' : 'middle'}
                                    height={revenueTrend.length > 10 ? 60 : 30}
                                />
                                <YAxis tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: '1px solid ' + C.border }}
                                    formatter={v => [fmt(v, currency), t('revenue')]}
                                />
                                <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                                    {revenueTrend.map((_, i) => (
                                        <Cell key={'rc-' + i} fill={i === revenueTrend.length - 1 ? C.green : C.greenBorder} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState title={t('no_revenue_data')} />
                    )}
                </SectionCard>

                <SectionCard>
                    <div style={sectionLabel}>{t('expense_breakdown')}</div>
                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 16 }}>
                        {t('expense_breakdown_description')}
                    </div>
                    {(overheadExpenses.length > 0 || dailyExpenses.length > 0) ? (
                        <div>
                            {overheadExpenses.length > 0 && (
                                <>
                                    <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 8 }}>
                                        {t('overhead') || 'Overhead'}
                                    </div>
                                    {overheadExpenses.map(item => (
                                        <BarListItem key={'oh-' + item.category} label={item.category} value={Number(item.total_cost)}
                                            maxValue={expenseMax} color={C.red} cur={currency}
                                            pct={expenseTotal > 0 ? (Number(item.total_cost) / expenseTotal) * 100 : 0} />
                                    ))}
                                </>
                            )}
                            {dailyExpenses.length > 0 && (
                                <>
                                    <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginTop: overheadExpenses.length > 0 ? 16 : 0, marginBottom: 8 }}>
                                        {t('daily') || 'Daily'}
                                    </div>
                                    {dailyExpenses.map(item => (
                                        <BarListItem key={'d-' + item.category} label={item.category} value={Number(item.total_cost)}
                                            maxValue={expenseMax} color={C.amber} cur={currency}
                                            pct={expenseTotal > 0 ? (Number(item.total_cost) / expenseTotal) * 100 : 0} />
                                    ))}
                                </>
                            )}
                        </div>
                    ) : (
                        <EmptyState title={t('no_expense_data')} />
                    )}
                </SectionCard>
            </div>

            {/* Payment Breakdown + Dining Split */}
            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <SectionCard>
                    <div style={sectionLabel}>{t('payment_breakdown')}</div>
                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 16 }}>
                        {t('payment_breakdown_description')}
                    </div>
                    {paymentData.length > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <div style={{ width: '50%' }}>
                                <ResponsiveContainer width="100%" height={180}>
                                    <PieChart>
                                        <Pie data={paymentData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2}>
                                            {paymentData.map((_, i) => <Cell key={'pc-' + i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: '1px solid ' + C.border }}
                                            formatter={v => [fmt(v, currency), t('amount')]}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div style={{ flex: 1 }}>
                                {paymentData.map((item, i) => (
                                    <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: 3, background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                                        <div>
                                            <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: C.text }}>{item.name}</div>
                                            <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted }}>
                                                {fmt(item.value, currency)} ({item.count} {t('transactions').toLowerCase()})
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <EmptyState title={t('no_payment_data')} />
                    )}
                </SectionCard>

                <SectionCard>
                    <div style={sectionLabel}>{t('dining_split')}</div>
                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 16 }}>
                        {t('dining_split_description')}
                    </div>
                    {diningItems.length > 0 ? (
                        <div>
                            {diningItems.map((item) => (
                                <BarListItem
                                    key={item.label}
                                    label={item.label}
                                    value={item.value}
                                    maxValue={diningMax}
                                    color={item.color}
                                    cur={currency}
                                    subtitle={`${item.count} ${t('orders').toLowerCase()}`}
                                />
                            ))}
                        </div>
                    ) : (
                        <EmptyState title={t('no_dining_data')} />
                    )}
                </SectionCard>
            </div>

            {/* Period Comparison (full width) */}
            <SectionCard style={{ marginBottom: 16 }}>
                <div style={sectionLabel}>{t('period_comparison')}</div>
                <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginBottom: 16 }}>
                    {t('period_comparison_description')}
                </div>
                {kpis.length > 0 ? (
                    isMobile ? (
                        <div>
                            {kpis.map((kpi, i) => {
                                const change = kpi.change_percent;
                                const positive = kpi.invert ? (change != null && change <= 0) : (change != null && change >= 0);
                                const isPct = kpi.is_percentage || kpi.name.includes('%') || kpi.name.includes('Margin');
                                const fmtVal = (v) => typeof v === 'number'
                                    ? (isPct ? v.toFixed(1) + '%' : fmt(v, currency))
                                    : (v != null ? v : '—');
                                return (
                                    <MobileCard
                                        key={i}
                                        collapsible={false}
                                        title={kpi.name}
                                        amount={fmtVal(kpi.value)}
                                        subtitle={`vs ${prevLabel}: ${fmtVal(kpi.previous_value)}`}
                                        status={{
                                            plain: true,
                                            tone: positive ? 'green' : 'red',
                                            icon: change != null ? (change >= 0 ? '↑ ' : '↓ ') : '',
                                            label: change != null ? `${Math.abs(change).toFixed(1)}%` : '—',
                                        }}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                    <div className="portal-table-wrap" style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid ' + C.border }}>
                                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: C.text }}>{t('metric') || 'Metric'}</th>
                                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: C.text }}>{currentLabel}</th>
                                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: C.muted }}>{prevLabel}</th>
                                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: C.text }}>{t('change') || 'Change'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {kpis.map((kpi, i) => {
                                    const change = kpi.change_percent;
                                    const positive = kpi.invert ? (change != null && change <= 0) : (change != null && change >= 0);
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid ' + C.border }}>
                                            <td style={{ padding: '10px 12px', fontWeight: 600, color: C.text }}>{kpi.name}</td>
                                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>
                                                {typeof kpi.value === 'number' ? (kpi.name.includes('%') || kpi.name.includes('Margin') ? kpi.value.toFixed(1) + '%' : fmt(kpi.value, currency)) : kpi.value}
                                            </td>
                                            <td style={{ padding: '10px 12px', textAlign: 'right', color: C.muted }}>
                                                {typeof kpi.previous_value === 'number' ? (kpi.name.includes('%') || kpi.name.includes('Margin') ? kpi.previous_value.toFixed(1) + '%' : fmt(kpi.previous_value, currency)) : '—'}
                                            </td>
                                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: positive ? C.green : C.red }}>
                                                {change != null ? `${change >= 0 ? '↑' : '↓'} ${Math.abs(change).toFixed(1)}%` : '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    )
                ) : (
                    <EmptyState title={t('no_data') || 'No data available'} />
                )}
            </SectionCard>

            {/* ═══ 7. Section Divider: Looking Ahead ═══ */}
            <SectionDivider label={t('looking_ahead')} tag={t('strategic')} tagColor={C.blue} tagBg={C.blueLight} />

            {/* ═══ 8. Strategic Layer ═══ */}
            {/* Revenue Forecast */}
            {showForecast && (
                <SectionCard style={{ marginBottom: 16 }}>
                    <div style={sectionLabel}>{t('revenue_forecast')}</div>
                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 16 }}>
                        {t('revenue_forecast_description')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
                        <span style={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, color: C.text }}>
                            {t('on_pace_for')} {fmt(projectedRevenue, currency)}
                        </span>
                    </div>
                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginBottom: 16 }}>
                        {t('confidence')}: {forecastConfidence}% ({daysElapsed}/{totalDays} {t('days_elapsed')})
                    </div>
                    {forecastChartData.length > 0 && (
                        <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={forecastChartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }}
                                    axisLine={false} tickLine={false}
                                    interval={Math.max(Math.floor(forecastChartData.length / 10), 0)}
                                />
                                <YAxis tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: '1px solid ' + C.border }}
                                    formatter={(v, name) => [fmt(v, currency), name === 'actual' ? t('actual') : t('forecast')]}
                                />
                                <Area
                                    dataKey="forecastHigh"
                                    stroke="none"
                                    fill={C.green}
                                    fillOpacity={0.08}
                                    connectNulls={false}
                                />
                                <Area
                                    dataKey="forecastLow"
                                    stroke="none"
                                    fill={C.white}
                                    fillOpacity={1}
                                    connectNulls={false}
                                />
                                <Line type="monotone" dataKey="actual" stroke={C.green} strokeWidth={2} dot={false} connectNulls={false} />
                                <Line type="monotone" dataKey="forecast" stroke={C.green} strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </SectionCard>
            )}

            {/* Targets Dashboard */}
            <SectionCard style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div>
                        <div style={sectionLabel}>{t('targets_dashboard')}</div>
                        <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12 }}>
                            {t('targets_dashboard_description')}
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setTargetForm({
                                revenue: '',
                                profit: '',
                                margin: '',
                                avg_transaction: '',
                            });
                            setTargetModal(true);
                        }}
                        style={{
                            fontFamily: FONT, fontSize: 12, fontWeight: 700,
                            padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                            background: C.green, color: C.white, border: 'none',
                            transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                        {t('set_targets')}
                    </button>
                </div>
                {targetsList.length > 0 ? (
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 16 }}>
                        {targetsList.map((target, i) => {
                            const { pct, actual, goal } = getTargetProgress(target);
                            const color = getTargetColor(pct);
                            return (
                                <div key={target.name || i} style={{
                                    padding: '16px 18px', borderRadius: 10,
                                    border: '1.5px solid ' + C.border, background: C.bg,
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.text }}>
                                            {target.name}
                                        </span>
                                        <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 800, color }}>
                                            {pct}%
                                        </span>
                                    </div>
                                    <div style={{ height: 8, background: C.white, borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                                        <div style={{ height: '100%', width: pct + '%', background: color, borderRadius: 4, transition: 'width 0.3s' }} />
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted }}>
                                            {fmt(actual, currency)} {t('of')} {fmt(goal, currency)}
                                        </span>
                                        <span style={{ fontFamily: FONT, fontSize: 11, color, fontWeight: 600 }}>
                                            {pct >= 75 ? t('on_track') : pct >= 50 ? t('needs_attention') : t('behind')}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px 20px', marginTop: 16 }}>
                        <div style={{ marginBottom: 12, opacity: 0.5 }}><TargetIcon /></div>
                        <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginBottom: 12 }}>
                            {t('set_first_target')}
                        </div>
                        <button
                            onClick={() => setTargetModal(true)}
                            style={{
                                fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
                                background: C.green, color: C.white, border: 'none',
                            }}
                        >
                            {t('set_targets')}
                        </button>
                    </div>
                )}
            </SectionCard>

            {/* Profit Waterfall */}
            <SectionCard style={{ marginBottom: 28 }}>
                <div style={sectionLabel}>{t('profit_waterfall')}</div>
                <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 20 }}>
                    {t('profit_waterfall_description')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {/* Revenue */}
                    <div style={{
                        padding: '16px 20px', borderRadius: 10, textAlign: 'center', minWidth: 120,
                        background: C.greenLight, border: '1.5px solid ' + C.greenBorder,
                    }}>
                        <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.green, marginBottom: 6 }}>
                            {t('revenue')}
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: C.green }}>
                            {fmt(totalRevenue, currency)}
                        </div>
                    </div>

                    {/* Arrow */}
                    <span style={{ fontFamily: FONT, fontSize: 20, color: C.mutedLight }}>→</span>

                    {/* Salary */}
                    <div style={{
                        padding: '16px 20px', borderRadius: 10, textAlign: 'center', minWidth: 120,
                        background: C.redLight, border: '1.5px solid #fecaca',
                    }}>
                        <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.red, marginBottom: 6 }}>
                            {t('salary')}
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: C.red }}>
                            -{fmt(waterfallSalary, currency)}
                        </div>
                    </div>

                    <span style={{ fontFamily: FONT, fontSize: 20, color: C.mutedLight }}>→</span>

                    {/* Shopping */}
                    <div style={{
                        padding: '16px 20px', borderRadius: 10, textAlign: 'center', minWidth: 120,
                        background: C.redLight, border: '1.5px solid #fecaca',
                    }}>
                        <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.red, marginBottom: 6 }}>
                            {t('shopping')}
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: C.red }}>
                            -{fmt(waterfallShopping, currency)}
                        </div>
                    </div>

                    <span style={{ fontFamily: FONT, fontSize: 20, color: C.mutedLight }}>→</span>

                    {/* Overhead */}
                    <div style={{
                        padding: '16px 20px', borderRadius: 10, textAlign: 'center', minWidth: 120,
                        background: C.redLight, border: '1.5px solid #fecaca',
                    }}>
                        <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.red, marginBottom: 6 }}>
                            {t('overhead')}
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: C.red }}>
                            -{fmt(waterfallOverhead, currency)}
                        </div>
                    </div>

                    {/* Equals */}
                    <span style={{ fontFamily: FONT, fontSize: 20, color: C.mutedLight }}>=</span>

                    {/* Net Profit */}
                    <div style={{
                        padding: '16px 20px', borderRadius: 10, textAlign: 'center', minWidth: 120,
                        background: C.blueLight, border: '1.5px solid ' + C.blueBorder,
                    }}>
                        <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.blue, marginBottom: 6 }}>
                            {t('net_profit')}
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: netProfit >= 0 ? C.blue : C.red }}>
                            {fmt(netProfit, currency)}
                        </div>
                    </div>
                </div>
            </SectionCard>

            {/* ═══ Expand Chart Modal ═══ */}
            {chartModal && (
                <div
                    onClick={() => setChartModal(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Escape') setChartModal(false);
                        if (e.key === 'ArrowLeft') setModalOffset(o => o - 1);
                        if (e.key === 'ArrowRight') setModalOffset(o => Math.min(o + 1, 0));
                    }}
                    tabIndex={0}
                    ref={el => el && el.focus()}
                >
                    <div
                        className="portal-modal-card"
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: C.white, borderRadius: 16, padding: 28,
                            width: '90vw', maxWidth: 1100, position: 'relative',
                        }}
                    >
                        <button
                            onClick={() => setChartModal(false)}
                            style={{
                                position: 'absolute', top: 16, right: 16,
                                background: 'none', border: 'none', cursor: 'pointer',
                                fontFamily: FONT, fontSize: 20, color: C.muted, lineHeight: 1,
                            }}
                        >&#x2715;</button>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                            <button
                                onClick={() => setModalOffset(o => o - 1)}
                                style={{
                                    background: C.bg, border: '1.5px solid ' + C.border, borderRadius: 8,
                                    padding: '8px 14px', cursor: 'pointer', fontFamily: FONT, fontSize: 13,
                                    fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 6,
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                                {t('previous')}
                            </button>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ ...sectionLabel, marginBottom: 4 }}>{t('revenue_trend')}</div>
                                <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: C.text }}>
                                    {modalRangeLabel}
                                </div>
                                {modalOffset !== 0 && (
                                    <button
                                        onClick={() => setModalOffset(0)}
                                        style={{
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            fontFamily: FONT, fontSize: 11, color: C.green, fontWeight: 600, marginTop: 4,
                                        }}
                                    >{t('back_to_current')}</button>
                                )}
                            </div>
                            <button
                                onClick={() => setModalOffset(o => Math.min(o + 1, 0))}
                                disabled={modalOffset >= 0}
                                style={{
                                    background: C.bg, border: '1.5px solid ' + C.border, borderRadius: 8,
                                    padding: '8px 14px', cursor: modalOffset >= 0 ? 'not-allowed' : 'pointer',
                                    fontFamily: FONT, fontSize: 13, fontWeight: 600,
                                    color: modalOffset >= 0 ? C.mutedLight : C.text,
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    opacity: modalOffset >= 0 ? 0.5 : 1,
                                }}
                            >
                                {t('next')}
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            </button>
                        </div>

                        {modalLoading ? (
                            <div style={{ textAlign: 'center', padding: '80px 0', fontFamily: FONT, fontSize: 14, color: C.muted }}>{t('loading')}...</div>
                        ) : modalData && modalData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={modalData} margin={{ top: 0, right: 20, left: 0, bottom: modalData.length > 10 ? 30 : 0 }}>
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fontFamily: FONT, fontSize: 11, fill: C.muted }}
                                        axisLine={false} tickLine={false}
                                        interval={modalData.length > 20 ? 2 : 0}
                                        angle={modalData.length > 10 ? -45 : 0}
                                        textAnchor={modalData.length > 10 ? 'end' : 'middle'}
                                        height={modalData.length > 10 ? 60 : 30}
                                    />
                                    <YAxis tick={{ fontFamily: FONT, fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                                    <Tooltip
                                        contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: '1px solid ' + C.border }}
                                        formatter={v => [fmt(v, currency), t('revenue')]}
                                    />
                                    <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                                        {modalData.map((_, i) => (
                                            <Cell key={'mc-' + i} fill={i === modalData.length - 1 ? C.green : C.greenBorder} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <EmptyState title={t('no_revenue_data')} />
                        )}
                    </div>
                </div>
            )}

            {/* ═══ Target Setting Modal ═══ */}
            {targetModal && (
                <div
                    onClick={() => setTargetModal(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <div
                        className="portal-modal-card"
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: C.white, borderRadius: 16, padding: 28,
                            width: '90vw', maxWidth: 480, position: 'relative',
                        }}
                    >
                        <button
                            onClick={() => setTargetModal(false)}
                            style={{
                                position: 'absolute', top: 16, right: 16,
                                background: 'none', border: 'none', cursor: 'pointer',
                                fontFamily: FONT, fontSize: 20, color: C.muted, lineHeight: 1,
                            }}
                        >&#x2715;</button>

                        <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 20 }}>
                            {t('set_targets')}
                        </div>

                        {[
                            { key: 'revenue', label: t('revenue_target'), rec: recommendations.revenue },
                            { key: 'profit', label: t('profit_target'), rec: recommendations.profit },
                            { key: 'margin', label: t('margin_target'), rec: recommendations.margin },
                            { key: 'avg_transaction', label: t('avg_transaction_target'), rec: recommendations.avg_transaction },
                        ].map(field => (
                            <div key={field.key} style={{ marginBottom: 16 }}>
                                <label style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: C.text, display: 'block', marginBottom: 6 }}>
                                    {field.label}
                                </label>
                                <input
                                    type="number"
                                    value={targetForm[field.key]}
                                    onChange={e => setTargetForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                                    placeholder={field.rec ? `${t('recommended')}: ${field.rec}` : ''}
                                    style={{
                                        fontFamily: FONT, fontSize: 13, padding: '10px 14px',
                                        border: '1.5px solid ' + C.border, borderRadius: 8,
                                        width: '100%', boxSizing: 'border-box', outline: 'none',
                                    }}
                                />
                            </div>
                        ))}

                        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                            <button
                                onClick={() => setTargetModal(false)}
                                style={{
                                    flex: 1, fontFamily: FONT, fontSize: 13, fontWeight: 700,
                                    padding: '10px 0', borderRadius: 8, cursor: 'pointer',
                                    background: C.white, color: C.text, border: '1.5px solid ' + C.border,
                                }}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={handleSaveTargets}
                                style={{
                                    flex: 1, fontFamily: FONT, fontSize: 13, fontWeight: 700,
                                    padding: '10px 0', borderRadius: 8, cursor: 'pointer',
                                    background: C.green, color: C.white, border: 'none',
                                }}
                            >
                                {t('save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ Responsive Styles ═══ */}
            <style>{`
                @media (max-width: 900px) {
                    [style*="repeat(6, 1fr)"] { grid-template-columns: repeat(3, 1fr) !important; }
                    [style*="1fr 1fr"] { grid-template-columns: 1fr !important; }
                }
                @media (max-width: 600px) {
                    [style*="repeat(6, 1fr)"], [style*="repeat(3, 1fr)"] { grid-template-columns: repeat(2, 1fr) !important; }
                }
            `}</style>
        </div>
    );
}
